/**
 * Складской дашборд — Сводка.
 *
 * Hero: общая стоимость остатков (по закупочным ценам) + оборот за период.
 * KPI: позиций, в наличии, ноль, низкий остаток, оборот приход/отгрузка/маржа.
 * Графики: динамика по месяцам, ABC-топ позиций, топ поставщиков/покупателей.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

import { Icon } from "@/components/prototype/Icon";
import { Spinner } from "@/components/Spinner";
import { PeriodPicker } from "@/components/PeriodPicker";
import {
  fetchWarehouseOverview, formatRub, monthLabel, ABC_COLORS,
} from "@/api/warehouse";
import { ApiError } from "@/api/client";
import { usePeriod } from "@/state/period";


export function WarehousePage() {
  const { from, to } = usePeriod();
  const [warehouseFilter, setWarehouseFilter] = useState<"" | "СКЛАД 2_4" | "ИНСТРУМЕНТ 1.0" | "ОФИС">("");

  const q = useQuery({
    queryKey: ["warehouse-overview", from, to, warehouseFilter],
    queryFn: () => fetchWarehouseOverview({
      date_from: from, date_to: to,
      warehouse: warehouseFilter || undefined,
    }),
  });

  if (q.isLoading) return <Spinner size="lg" label="Загрузка складских данных…" />;
  if (q.error) {
    const err = q.error;
    if (err instanceof ApiError && err.status === 403) {
      return (
        <div className="alert alert-amber">
          <div className="alert-icon"><Icon name="warn" size={16}/></div>
          <div className="alert-body">
            <div className="alert-title">Доступ ограничен</div>
            <div>Складской раздел доступен админу, руководителю или кладовщику.</div>
          </div>
        </div>
      );
    }
    const msg = err instanceof ApiError ? `HTTP ${err.status}` : (err as Error).message;
    return <div className="alert alert-red"><div className="alert-body">{msg}</div></div>;
  }
  const ov = q.data!;
  const s = ov.stocks;
  const t = ov.turnover;

  const monthlyData = ov.by_month.map(m => ({
    month: monthLabel(m.month),
    inflow: m.inflow,
    outflow: m.outflow,
    margin: m.margin,
  }));

  return (
    <div className="page">

      {/* Hero */}
      <div className="hero hero-overview">
        <div>
          <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--mute-2)", marginBottom: 14 }}>
            Складской учёт · {ov.period.from} — {ov.period.to}
            {warehouseFilter && <> · <span style={{ color: "var(--brick-soft)" }}>{warehouseFilter}</span></>}
          </div>
          <div className="hero-headline">
            <span style={{ color: "var(--brick-soft)" }}>
              {formatRub(s.value_buy, { unit: false })}
            </span><br />
            <span style={{ fontSize: "0.55em", color: "var(--mute-2)" }}>
              стоимость остатков (закуп) · {s.positions} позиций
            </span>
          </div>
          <div style={{ marginTop: 14, fontSize: 13, color: "var(--mute-2)", maxWidth: 520, lineHeight: 1.5 }}>
            Данные на 01.01.2025 (импорт из «Складской учет 20»). История движений за 5 лет (2021—2025).
          </div>
        </div>

        <div className="hero-stat">
          <div className="hero-stat-label">Оборот за период (отгрузка)</div>
          <div className="hero-stat-value">
            {formatRub(t.outflow.amount, { unit: false })}
            <span className="hero-stat-unit">
              {Math.abs(t.outflow.amount) >= 1_000_000 ? "млн ₽" : "тыс ₽"}
            </span>
          </div>
          <div style={{ marginTop: 8, fontSize: 12, color: "var(--mute-2)" }}>
            {t.outflow.docs} накладных
          </div>
        </div>

        <div className="hero-stat">
          <div className="hero-stat-label">Маржа</div>
          <div className="hero-stat-value">
            {t.outflow.margin_pct === null ? "—" : (t.outflow.margin_pct ?? 0).toFixed(1).replace(".", ",")}
            <span className="hero-stat-unit">{t.outflow.margin_pct === null ? "" : "%"}</span>
          </div>
          <div style={{ marginTop: 8, fontSize: 12, color: "var(--mute-2)" }}>
            {formatRub(t.outflow.margin || 0)}
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="filter-bar" style={{ justifyContent: "space-between", marginBottom: "var(--gap)" }}>
        <div className="chip-row">
          <button className={`chip ${!warehouseFilter ? "active" : ""}`} onClick={() => setWarehouseFilter("")}>Все склады</button>
          <button className={`chip ${warehouseFilter === "СКЛАД 2_4" ? "active" : ""}`} onClick={() => setWarehouseFilter("СКЛАД 2_4")}>СКЛАД 2_4</button>
          <button className={`chip ${warehouseFilter === "ИНСТРУМЕНТ 1.0" ? "active" : ""}`} onClick={() => setWarehouseFilter("ИНСТРУМЕНТ 1.0")}>Инструмент</button>
        </div>
        <PeriodPicker />
      </div>

      {/* KPI row */}
      <div className="row row-4" style={{ marginBottom: "var(--gap)" }}>
        <div className="kpi">
          <div className="kpi-label">Позиций</div>
          <div className="kpi-value kpi-value-sm">{s.positions.toLocaleString("ru-RU")}</div>
          <div className="kpi-foot"><span style={{ fontSize: 12, color: "var(--mute)" }}>в каталоге</span></div>
        </div>
        <div className="kpi good">
          <div className="kpi-label">В наличии</div>
          <div className="kpi-value kpi-value-sm">{s.in_stock.toLocaleString("ru-RU")}</div>
          <div className="kpi-foot"><span style={{ fontSize: 12, color: "var(--mute)" }}>с остатком &gt; 0</span></div>
        </div>
        <div className={`kpi ${s.low_stock > 0 ? "alarm" : ""}`}>
          <div className="kpi-label">Ниже критич.</div>
          <div className="kpi-value kpi-value-sm">{s.low_stock}</div>
          <div className="kpi-foot">
            {s.low_stock > 0 ? (
              <Link to={"/stocks?low=1" as "/"} className="card-link" style={{ fontSize: 12, fontWeight: 600 }}>
                Посмотреть список <Icon name="chevron_r" size={10}/>
              </Link>
            ) : (
              <span style={{ fontSize: 12, color: "var(--mute)" }}>всё в норме</span>
            )}
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Без остатка</div>
          <div className="kpi-value kpi-value-sm">{s.zero_stock}</div>
          <div className="kpi-foot"><span style={{ fontSize: 12, color: "var(--mute)" }}>quantity ≤ 0</span></div>
        </div>
      </div>

      {/* Turnover row */}
      <div className="row row-3" style={{ marginBottom: "var(--gap)" }}>
        <div className="kpi">
          <div className="kpi-label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Icon name="arrow_down" size={12}/> Приход
          </div>
          <div className="kpi-value kpi-value-sm">
            {formatRub(t.inflow.amount, { unit: false })}
            <span className="kpi-unit">{Math.abs(t.inflow.amount) >= 1_000_000 ? " млн ₽" : " тыс ₽"}</span>
          </div>
          <div className="kpi-foot"><span style={{ fontSize: 12, color: "var(--mute)" }}>{t.inflow.docs} накладных</span></div>
        </div>
        <div className="kpi">
          <div className="kpi-label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Icon name="arrow_up" size={12}/> Отгрузка
          </div>
          <div className="kpi-value kpi-value-sm">
            {formatRub(t.outflow.amount, { unit: false })}
            <span className="kpi-unit">{Math.abs(t.outflow.amount) >= 1_000_000 ? " млн ₽" : " тыс ₽"}</span>
          </div>
          <div className="kpi-foot"><span style={{ fontSize: 12, color: "var(--mute)" }}>{t.outflow.docs} накладных</span></div>
        </div>
        <div className={`kpi ${(t.outflow.margin ?? 0) >= 0 ? "good" : "alarm"}`}>
          <div className="kpi-label">Маржа</div>
          <div className="kpi-value kpi-value-sm">
            {formatRub(t.outflow.margin ?? 0, { unit: false })}
            <span className="kpi-unit">{Math.abs(t.outflow.margin ?? 0) >= 1_000_000 ? " млн ₽" : " тыс ₽"}</span>
          </div>
          <div className="kpi-foot">
            <span style={{ fontSize: 12, color: "var(--mute)" }}>
              {t.outflow.margin_pct === null ? "—" : `${t.outflow.margin_pct}%`}
            </span>
          </div>
        </div>
      </div>

      {/* По складам */}
      {s.by_warehouse.length > 1 && (
        <div className="card" style={{ marginBottom: "var(--gap)" }}>
          <div className="card-h">
            <div>
              <h3 className="card-title">По складам</h3>
              <div className="card-sub">Позиции и стоимость по каждому складу</div>
            </div>
          </div>
          <div className="tbl-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Склад</th>
                  <th className="num">Позиций</th>
                  <th className="num">Общее кол-во</th>
                  <th className="num">Стоимость (закуп)</th>
                </tr>
              </thead>
              <tbody>
                {s.by_warehouse.map(w => (
                  <tr key={w.warehouse}>
                    <td className="strong">{w.warehouse}</td>
                    <td className="num tabular">{w.positions.toLocaleString("ru-RU")}</td>
                    <td className="num tabular">{Math.round(w.quantity).toLocaleString("ru-RU")}</td>
                    <td className="num strong tabular">{formatRub(w.value_buy)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Динамика по месяцам */}
      {monthlyData.length > 0 && (
        <div className="card" style={{ marginBottom: "var(--gap)" }}>
          <div className="card-h">
            <div>
              <h3 className="card-title">Движение по месяцам</h3>
              <div className="card-sub">Приходы и отгрузки в деньгах</div>
            </div>
          </div>
          <div style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={monthlyData} margin={{ top: 12, right: 16, bottom: 8, left: 8 }}>
                <CartesianGrid stroke="var(--line-soft)" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "var(--mute)" }} interval="preserveStartEnd"/>
                <YAxis
                  tick={{ fontSize: 11, fill: "var(--mute)" }}
                  tickFormatter={(v: number) => v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}м` : `${(v / 1000).toFixed(0)}т`}
                />
                <Tooltip
                  formatter={(v: number) => formatRub(v)}
                  contentStyle={{ background: "var(--card)", border: "1px solid var(--line)", borderRadius: 8, fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                <Bar dataKey="inflow"  name="Приход"   fill="#3D7A3D" radius={[4, 4, 0, 0]} />
                <Bar dataKey="outflow" name="Отгрузка" fill="#AB2B2B" radius={[4, 4, 0, 0]} />
                <Bar dataKey="margin"  name="Маржа"    fill="#1A1614" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ABC топ-позиций */}
      <div className="row row-2">
        <div className="card">
          <div className="card-h">
            <div>
              <h3 className="card-title">Топ-10 отгрузок (ABC)</h3>
              <div className="card-sub">По обороту в деньгах</div>
            </div>
            <Link to={"/outflows" as "/"} className="card-link">Все <Icon name="chevron_r" size={12}/></Link>
          </div>
          {ov.top_outflow_items.length > 0 ? (
            <div className="tbl-scroll">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Позиция</th>
                    <th className="num">Кол-во</th>
                    <th className="num">Сумма</th>
                    <th>ABC</th>
                  </tr>
                </thead>
                <tbody>
                  {ov.top_outflow_items.map((it, i) => (
                    <tr key={i}>
                      <td>
                        <div className="strong">{it.name}</div>
                        {it.sku && <div style={{ fontSize: 11, color: "var(--mute)" }}>{it.sku}</div>}
                      </td>
                      <td className="num tabular">{Math.round(it.quantity).toLocaleString("ru-RU")} {it.unit || ""}</td>
                      <td className="num strong tabular">{formatRub(it.amount)}</td>
                      <td>
                        <span className="badge" style={{ background: ABC_COLORS[it.abc], color: "#fff", padding: "2px 8px", borderRadius: 4 }}>
                          {it.abc}
                        </span>
                        <span style={{ fontSize: 10, color: "var(--mute)", marginLeft: 6 }}>{it.share_pct}%</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div className="muted" style={{ padding: 20 }}>Нет данных за период</div>}
        </div>

        <div className="card">
          <div className="card-h">
            <div>
              <h3 className="card-title">Топ-10 поставщиков</h3>
              <div className="card-sub">По сумме приходов</div>
            </div>
            <Link to={"/inflows" as "/"} className="card-link">Все <Icon name="chevron_r" size={12}/></Link>
          </div>
          {ov.top_suppliers.length > 0 ? (
            <div className="tbl-scroll">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Поставщик</th>
                    <th className="num">Накладных</th>
                    <th className="num">Сумма</th>
                  </tr>
                </thead>
                <tbody>
                  {ov.top_suppliers.map((c, i) => (
                    <tr key={i}>
                      <td className="strong">{c.name}</td>
                      <td className="num tabular">{c.docs}</td>
                      <td className="num strong tabular">{formatRub(c.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div className="muted" style={{ padding: 20 }}>Нет данных</div>}
        </div>
      </div>
    </div>
  );
}
